package structs.account

import kotlinx.serialization.Serializable

/**
 * A single additive subscription add-on row attached to a
 * [BillingStatus]. The data shape is "ephemeral upgrade": the
 * player's effective subscription surface is the union / sum of
 * their base `plan` AND every unexpired row in `addOns`.
 *
 * Promo-code redemption appends one row per grant (or extends an
 * matching existing row when the same code is redeemed twice
 * within the same window -- see [effectiveSubscription] for the
 * dedupe rule).
 *
 * @property plan The tier this add-on carries. Independent of
 *   the player's base `plan`. A REPUBLIC player can hold an EMPIRE
 *   add-on; the EMPIRE subset applies additively for the add-on's
 *   window, then expires.
 * @property daysRemaining Lifetime in calendar days from issue.
 *   Drives `expiresMillis`. Positive integer; never decremented at
 *   persistence time (we use expires-as-instant, not counters).
 * @property grantedAtMillis Epoch millis when the row was
 *   appended. Saved for audit, but resolution reads only
 *   `expiresMillis`.
 * @property expiresMillis Hard expiry. Once `nowMillis` exceeds
 *   this, the add-on is filtered out of the effective surface.
 *   Set to `grantedAtMillis + daysRemaining * 86_400_000L`.
 * @property unlocksModesBoost Optional set of modes the add-on
 *   promotes. `null` means "no mode-boost beyond the base plan".
 *   `setOf(ONE_V_THREE)` on a REPUBLIC player unlocks 1v3.
 */
@Serializable
data class SubscriptionAddOn(
    val plan: AccountPlan,
    val daysRemaining: Int,
    val grantedAtMillis: Long,
    val expiresMillis: Long,
    val unlocksModesBoost: Set<GameMode>? = null,
)
